import React from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../ui/Button/Button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator
} from '../ui/Dropdown/Dropdown';
import { Avatar } from '../ui/Avatar/Avatar';
import { Badge } from '../ui/Badge/Badge';
import { useTheme, type Theme } from '../../lib/theme';
import { AccountService } from '../../services/AccountService';
import { t } from '../../lib/i18n';

const accountSvc = new AccountService();

export function UserMenu() {
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const isLoading = useAuthStore(s => s.isLoading);
  const isSuperAdmin = !!(user as any)?.isSuperAdmin;
  const navigate = useNavigate();
  const { theme: currentTheme, setTheme: setCurrentTheme, effectiveTheme } = useTheme();

  // B10: Persist theme change to server so it survives user switching across browsers.
  const handleThemeChange = React.useCallback(async (newTheme: Theme) => {
    setCurrentTheme(newTheme);
    try {
      await accountSvc.updatePreferences({ theme_preference: newTheme });
    } catch {
      // Preference update is best-effort; localStorage already updated so UI is correct.
    }
  }, [setCurrentTheme]);

  // Note: Organization data is now initialized from bootstrap in the router loader
  // This prevents redundant API calls that caused infinite loops

  if (!user) return null;

  const userInitial = user.fullName?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="flex items-center gap-2">
          <Avatar
            size="sm"
            src={user.avatarUrl || undefined}
            initials={userInitial}
            name={user.fullName || user.email}
          />
          <span className="font-medium hidden sm:inline">{user.fullName || user.email}</span>
          <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-3 py-2">
          <div className="text-sm font-medium">{user.fullName}</div>
          <div className="text-xs text-muted-foreground">{user.email}</div>
          {!user.isEmailVerified && (
            <Badge variant="warning" className="mt-2 text-xs">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Email not verified
            </Badge>
          )}
        </div>
        {isSuperAdmin && (
          <DropdownMenuItem asChild>
            <Link to="/admin" className="flex items-center cursor-pointer">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" />
              </svg>
              Global Settings
            </Link>
          </DropdownMenuItem>
        )}
        {/* "Settings" entry removed — organization settings now live in the
            ROROS sidebar under Configuration → System Settings. */}
        <DropdownMenuItem asChild>
          <Link to="/settings/account/profile" className="flex items-center cursor-pointer">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A7 7 0 0112 15a7 7 0 016.879 2.804M15 11a3 3 0 10-6 0 3 3 0 006 0z" />
            </svg>
            Account Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-3 py-2">
          <div className="text-xs mb-1.5 text-muted-foreground">{t('Theme')}</div>
          <div className="flex gap-1" role="group" aria-label={t('Theme')}>
            {([
              { value: 'light' as Theme, icon: (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ), labelKey: 'Light' as const },
              { value: 'dark' as Theme, icon: (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ), labelKey: 'Dark' as const },
              { value: 'system' as Theme, icon: (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              ), labelKey: 'System' as const },
            ] as const).map(({ value, icon, labelKey }) => (
              <Button
                key={value}
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleThemeChange(value); }}
                aria-pressed={currentTheme === value}
                aria-label={t(labelKey)}
                className={`flex items-center gap-1 px-2 py-1 h-auto text-xs transition-colors ${
                  currentTheme === value
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {icon}
                {t(labelKey)}
              </Button>
            ))}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => {
            try {
              await logout();
            } finally {
              navigate('/auth', { replace: true });
            }
          }}
          disabled={isLoading}
          className="cursor-pointer"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          {isLoading ? 'Signing out...' : 'Sign out'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
